import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { tap } from 'rxjs/operators';
import { ActionCreatorWithProps } from 'src/app/shared/types/action-creator.type';
import { AnalyticsEvent, AnalyticsService } from '../analytics.service';
import { ActionFilterArgs } from '../../types/action-filter.type';
import { filterActions } from '../../rxjs-operators/filter-actions.operator';
import { FeatureFlagFilterArgs } from '../../types/feature-flag-filter.type';
import { FeatureFlagFilterOperatorService } from '../../rxjs-operators/feature-flag-filter-operator.service';

export type AnalyticsTrackingEffectCreatorArgs<TActionProps> = {
  initiatingAction: ActionCreatorWithProps<TActionProps>;
  event: AnalyticsEvent;
  properties?: (actionPayload: TActionProps) => Record<string, unknown>;
} & ActionFilterArgs<TActionProps> &
  FeatureFlagFilterArgs;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsTrackingEffectCreatorService {
  constructor(
    private actions$: Actions,
    private analyticsService: AnalyticsService,
    private featureFlagOperatorService: FeatureFlagFilterOperatorService,
  ) {}

  /**
   * Creates an effect which makes an analytics tracking call with the specified event and properties
   * @param options options for the effect
   * @param options.initiatingAction action which triggers the tracking call
   * @param options.event details of the event to track
   * @param options.properties optional properties to add to the tracking call
   */
  createEffect<TActionProps>(options: AnalyticsTrackingEffectCreatorArgs<TActionProps>) {
    const { initiatingAction, featureFlag, actionFilter, event, properties } = options;

    return createEffect(
      () =>
        this.actions$.pipe(
          ofType(initiatingAction),
          this.featureFlagOperatorService.filter(featureFlag),
          filterActions(actionFilter),
          tap((actionProps) => this.analyticsService.track(event, properties?.(actionProps))),
        ),
      { dispatch: false },
    );
  }
}
